import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Sparkles, Send, Loader2, User, Bot, RefreshCw, 
  Mail, FileText, TrendingUp, Lightbulb, Copy, Check
} from 'lucide-react';
import * as api from '../services/api';

const AIAssistantPanel = ({ currentUser, crmContext = {} }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! Soy tu asistente de ventas con IA. Puedo ayudarte a:\n\n• Redactar emails profesionales\n• Generar propuestas comerciales\n• Analizar datos de ventas\n• Sugerir acciones de seguimiento\n• Mejorar tus textos\n\n¿En qué puedo ayudarte hoy?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.aiAssistant(
        userMessage,
        crmContext,
        messages.slice(-10) // Send last 10 messages for context
      );

      if (response.data.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.response 
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: '❌ Lo siento, hubo un error. Por favor intenta de nuevo.',
          error: true
        }]);
      }
    } catch (err) {
      console.error('AI error:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ Error de conexión. Verifica que la API key de Gemini esté configurada en Admin > API Keys.',
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (action) => {
    let prompt = '';
    switch (action) {
      case 'email':
        prompt = 'Ayúdame a redactar un email profesional de seguimiento para un cliente potencial';
        break;
      case 'proposal':
        prompt = 'Necesito generar una propuesta comercial para un cliente nuevo';
        break;
      case 'analysis':
        prompt = 'Analiza los datos de ventas actuales y dame recomendaciones';
        break;
      case 'followup':
        prompt = 'Sugiere las mejores acciones de seguimiento para cerrar más negocios';
        break;
      default:
        return;
    }
    setInput(prompt);
  };

  const copyToClipboard = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: '¡Chat reiniciado! ¿En qué puedo ayudarte?'
    }]);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] max-h-[700px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Asistente de Ventas IA</h2>
            <p className="text-xs text-gray-500">Powered by Gemini</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={clearChat}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Reiniciar
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handleQuickAction('email')}
          className="text-xs"
        >
          <Mail className="h-3 w-3 mr-1" />
          Redactar Email
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handleQuickAction('proposal')}
          className="text-xs"
        >
          <FileText className="h-3 w-3 mr-1" />
          Propuesta
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handleQuickAction('analysis')}
          className="text-xs"
        >
          <TrendingUp className="h-3 w-3 mr-1" />
          Análisis
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handleQuickAction('followup')}
          className="text-xs"
        >
          <Lightbulb className="h-3 w-3 mr-1" />
          Seguimiento
        </Button>
      </div>

      {/* Messages */}
      <Card className="flex-1 overflow-hidden">
        <CardContent className="p-0 h-full flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div 
                key={idx}
                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user' 
                    ? 'bg-[#D4A843] text-white' 
                    : 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                }`}>
                  {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                </div>
                <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`p-3 rounded-lg ${
                    msg.role === 'user' 
                      ? 'bg-[#D4A843] text-white rounded-br-none' 
                      : msg.error 
                        ? 'bg-red-50 text-red-800 rounded-bl-none'
                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.role === 'assistant' && !msg.error && (
                    <button 
                      onClick={() => copyToClipboard(msg.content, idx)}
                      className="mt-1 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                    >
                      {copied === idx ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      {copied === idx ? 'Copiado' : 'Copiar'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-gray-100 p-3 rounded-lg rounded-bl-none">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t p-4">
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu pregunta o solicitud..."
                disabled={loading}
                className="flex-1"
              />
              <Button 
                type="submit" 
                disabled={loading || !input.trim()}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIAssistantPanel;
